import React, { useEffect, useState } from 'react';
import { Button, Collapse } from 'reactstrap';
import PropTypes from 'prop-types';
import Formsy from 'formsy-react';
import qs from 'qs';
import _ from 'lodash';
import Link from 'next/link';
import { faAngleLeft, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import getAxiosInstance from '../../config/api';
import TreeFilterFacet from './TreeFilterFacet';
import { ActiveButton, InactiveButton } from './FilterButtons';
import NavigationPanel from './NavigationPanel';

const ListFilter = ({ querySpecification, filter, onChange }) => {
  if (_.get(querySpecification, filter.filterKey) === filter.value) {
    return (
      <ActiveButton
        onClick={() => onChange(filter.filterKey, undefined)}
        filterKey={filter.filterKey}
        value={filter.displayValue || filter.value}
      />
    );
  }
  return (
    <InactiveButton
      onClick={() => onChange(filter.filterKey, filter.value)}
      filterKey={filter.filterKey}
      value={filter.displayValue || filter.value}
      count={filter.count}
    />
  );
};

ListFilter.propTypes = {
  querySpecification: PropTypes.object.isRequired,
  filter: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

const ListFacet = ({ facet, querySpecification, onChange }) => {
  const limitedNumberOfElements = 10;
  const [numberOfElementsToShow, setNumberOfElementsToShow] = useState(
    limitedNumberOfElements,
  );
  const [isOpen, setIsOpen] = useState(
    !!_.get(querySpecification, `${[facet.data[0]?.filterKey]}`),
  );

  const showHundred = () => {
    setNumberOfElementsToShow(100);
  };

  return (
    <div className="mb-2">
      <h6
        className="ps-2 pe-1 mb-2 fw-bold text-dark"
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer' }}
      >
        {facet.facetName}
        <span className="float-end">
          <FontAwesomeIcon icon={isOpen ? faAngleDown : faAngleLeft} />
        </span>
      </h6>
      <Collapse isOpen={isOpen}>
        {facet.data.slice(0, numberOfElementsToShow).map(filter => (
          <div className="ps-2" key={filter.filterKey + filter.value}>
            <ListFilter
              querySpecification={querySpecification}
              onChange={onChange}
              filter={filter}
            />
          </div>
        ))}
        {facet.data.length > numberOfElementsToShow && (
          <Button color="Link" onClick={showHundred}>
            Se fler alternativ
          </Button>
        )}
      </Collapse>
    </div>
  );
};

ListFacet.propTypes = {
  querySpecification: PropTypes.object.isRequired,
  facet: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

const ShortcutButton = ({ shortcut, className, textClassName }) => {
  return (
    <h6
      className={`mb-0 link-animated link-animated-light fw-light filter-button-inactive ${className}`}
    >
      <Link
        className={`mt-1 ${textClassName} text-decoration-none`}
        href={shortcut.link || ''}
        target={shortcut.openInNewTab ? '_blank' : '_self'}
        role="button"
      >
        {shortcut.name}
      </Link>
    </h6>
  );
};

ShortcutButton.propTypes = {
  shortcut: PropTypes.object.isRequired,
  className: PropTypes.string,
  textClassName: PropTypes.string,
};

ShortcutButton.defaultProps = {
  className: '',
  textClassName: '',
};

const FilterPanel = ({
  onChange,
  querySpecification,
  hasValueCallback,
  shopConfig,
  hideShortcuts,
  ref,
  style,
  articleListConfig,
}) => {
  const { articleFilterPanelConfig } = shopConfig;
  const [filterFacets, setFilterFacets] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProductCategoriesOpen, setIsProductCategoriesOpen] = useState(
    _.isEmpty(articleFilterPanelConfig?.shortcuts) ||
      !!_.get(querySpecification, 'productCategory'),
  );

  const [isOriginOpen, setIsOriginOpen] = useState(
    !!_.get(querySpecification, 'origin'),
  );
  const [isShortCutsOpen, setIsShortCutsOpen] = useState(
    articleListConfig?.showShortcuts || !hideShortcuts,
  );

  useEffect(() => {
    async function getFacets() {
      const queryStr = qs.stringify({
        query: querySpecification,
      });
      if (!isLoading) {
        setIsLoading(true);
        const axiosInstance = getAxiosInstance();
        const filterFacetData = (
          await axiosInstance.get(`/public/filterFacets?${queryStr}`)
        ).data;
        setFilterFacets(filterFacetData);
        hasValueCallback(filterFacetData.length > 0);
        setIsLoading(false);
      }
    }
    getFacets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querySpecification]);

  const handleChange = (name, value) => {
    if (_.isNull(value)) {
      onChange(_.omit(querySpecification, name));
      return;
    }
    onChange({
      ...querySpecification,
      [name]: value,
    });
  };

  const showShortcuts =
    (!_.isEmpty(shopConfig?.articleFilterPanelConfig?.shortcuts) &&
      !hideShortcuts) ||
    articleListConfig?.showShortcuts;

  return (
    <Formsy ref={ref} style={style} className="filterPanel">
      {showShortcuts &&
        !articleFilterPanelConfig?.integrateShortcutsInCategories && (
          <div className="mb-2">
            <h6
              className="ps-2 pe-1 mb-2 fw-bold text-dark"
              onClick={() => setIsShortCutsOpen(!isShortCutsOpen)}
              style={{ cursor: 'pointer' }}
            >
              {shopConfig?.articleFilterPanelConfig?.name || 'Genvägar'}
              <span className="float-end">
                <FontAwesomeIcon
                  icon={isShortCutsOpen ? faAngleDown : faAngleLeft}
                />
              </span>
            </h6>
            <Collapse isOpen={isShortCutsOpen}>
              {articleFilterPanelConfig?.shortcuts?.map(shortcut => (
                <ShortcutButton
                  key={shortcut.id}
                  shortcut={shortcut}
                  className="mb-1"
                  textClassName="px-2 text-muted"
                />
              ))}
            </Collapse>
          </div>
        )}
      {filterFacets &&
        filterFacets.map(facet => (
          <div key={facet.id}>
            {facet.facetName === 'Navigering' && (
              <NavigationPanel
                facets={facet.data}
                name={
                  shopConfig?.usesOrganizationProductCategories
                    ? 'organizationProductCategory'
                    : 'productCategory'
                }
                onChange={handleChange}
                querySpecification={querySpecification}
                articleListConfig={articleListConfig}
              >
                {articleFilterPanelConfig?.integrateShortcutsInCategories &&
                  articleFilterPanelConfig?.shortcuts?.map(shortcut => {
                    return (
                      <ShortcutButton
                        key={shortcut.id}
                        shortcut={shortcut}
                        textClassName="navigation-button-inactive ms-2 mb-1"
                      />
                    );
                  })}
              </NavigationPanel>
            )}
          </div>
        ))}

      {filterFacets && (
        <div className="sidebar-icon-menu mt-lg-0">
          <h4 className={`ps-2 mb-2 fw-light ${showShortcuts ? 'mt-3' : ''}`}>
            Filter
          </h4>

          {filterFacets.map(facet => {
            if (facet.facetName === 'Navigering') {
              return <></>;
            }
            if (facet.facetName === 'Produktkategori') {
              return (
                <div className="mb-2" key="facet-productcategory">
                  <h6
                    className="ps-2 pe-1 mb-2 fw-bold text-dark"
                    onClick={() =>
                      setIsProductCategoriesOpen(!isProductCategoriesOpen)
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    Kategorier
                    <span className="float-end">
                      <FontAwesomeIcon
                        icon={
                          isProductCategoriesOpen ? faAngleDown : faAngleLeft
                        }
                      />
                    </span>
                  </h6>
                  <Collapse isOpen={isProductCategoriesOpen}>
                    <TreeFilterFacet
                      name={
                        shopConfig?.usesOrganizationProductCategories
                          ? 'organizationProductCategory'
                          : 'productCategory'
                      }
                      tree={facet.data}
                      onChange={handleChange}
                      querySpecification={querySpecification}
                    />
                  </Collapse>
                </div>
              );
            }

            if (facet.facetName === 'Ursprung') {
              return (
                <div className="mb-2" key="facet-origin">
                  <h6
                    className="ps-2 pe-1 mb-2 fw-bold text-dark"
                    onClick={() => setIsOriginOpen(!isOriginOpen)}
                    style={{ cursor: 'pointer' }}
                  >
                    Ursprung
                    <span className="float-end">
                      <FontAwesomeIcon
                        icon={isOriginOpen ? faAngleDown : faAngleLeft}
                      />
                    </span>
                  </h6>
                  <Collapse isOpen={isOriginOpen}>
                    <TreeFilterFacet
                      name="origin"
                      tree={facet.data}
                      onChange={handleChange}
                      querySpecification={querySpecification}
                    />
                  </Collapse>
                </div>
              );
            }
            return (
              <ListFacet
                key={facet.facetName}
                querySpecification={querySpecification}
                onChange={handleChange}
                facet={facet}
              />
            );
          })}
        </div>
      )}
    </Formsy>
  );
};

FilterPanel.propTypes = {
  querySpecification: PropTypes.object,
  articleListConfig: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  hasValueCallback: PropTypes.func,
  shopConfig: PropTypes.object.isRequired,
  hideShortcuts: PropTypes.bool,
  ref: PropTypes.object,
  style: PropTypes.object,
};

FilterPanel.defaultProps = {
  querySpecification: {},
  hasValueCallback: x => x,
  hideShortcuts: false,
  ref: null,
  style: null,
};

export default FilterPanel;
